import React, { useMemo } from 'react';
import FullPage from '@/src/components/UI/FullPage';
import OpenGraph from '@/components/OpenGraph';
import { useAsync } from 'react-use';
import FunwooAPI, { BuildingShowcaseEntity, BuildingStateTag, GetOptionsForBuildingFilter } from '@/swagger';
import FunwooImage from '@/components/common/FunwooImage';
import Flexbox from '@/components/common/Flexbox';
import { CountryEnum } from '@/constants/country';
import { Button } from '@chakra-ui/react';
import classNames from 'classnames';
import Link from 'next/link';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { locationData } from '@/lib/constant';
import { capitalize, uniq } from 'lodash';
import { isNotEmptyString, isNotSet } from '@/utils/formatChecker';
import { isBefore } from 'date-fns';
import BuildingAreaFilter, { filterShowcasesByCity } from '@/src/components/UI/building/BuildingAreaFilter';
import { useRouter } from 'next/router';
import { usePriceByFloorPlan } from '@/pages/building/[...entity]';
import { chineseNumeralFormatter, dollarLabelFormatter } from '@/src/libs/number-helper';
import { sortListingPlanType } from '@/src/helper/listing';
import { BuildingSoldOutBadge, isBuildingSoldOut } from '@/src/components/UI/Badge';

interface Props {
  showcases: BuildingShowcaseEntity[];
  areaData: GetOptionsForBuildingFilter;
}

const Building: React.FC<Props> = ({ showcases, areaData }) => {
  const { value: location } = useAsync(() => locationData, []);
  const { cities, country } = useRouter().query as { cities: string[]; country: keyof typeof CountryEnum };

  const filteredShowcases = useMemo(() => {
    if (!country || country === 'ALL') return showcases;
    return filterShowcasesByCity(showcases, cities, location, country);
  }, [showcases, country, cities, location]);

  return (
    <React.Fragment>
      <FullPage title={'美國建案'} hasPadding hasBottomMargin={false} hasHeaderPlaceHolder>
        <OpenGraph
          title='FUNWOO 德載國際不動產 - 美國建案'
          type='funwoo.building'
          url='https://funwoo.com.tw/building'
        />
        <p className={'hidden invisible sm:block sm:visible py-4 text-2xl text-center font-medium'}>美國建案</p>
        <p className={'px-6 pt-3 pb-4 sm:py-0 font-noto text-lg font-medium text-gold text-center'}>
          <span>欲了解其它新建案， 請撥打</span>
          <br className={'sm:hidden'} />
          <a className={'underline'} href={'tel:02-7752-3755'}>
            02-7752-3755
          </a>
        </p>
        <BuildingAreaFilter showcases={showcases} areaData={areaData} />
        <div className={'py-4 sm:flex sm:flex-wrap sm:-mr-6'}>
          {filteredShowcases?.map((showcase) => (
            <Showcase
              key={showcase.id}
              className={classNames(
                'mb-8 last:mb-0 sm:last:mb-8 sm:mr-6',
                'w-full',
                'sm:w-[calc(50%-24px)]',
                'md:w-[calc((100%/3)-24px)]',
                'sm:flex-shrink-0'
              )}
              {...showcase}
            />
          ))}
        </div>
      </FullPage>
    </React.Fragment>
  );
};

export default Building;

export const getServerSideProps: GetServerSideProps<Props> = async ({ locale = '' }) => {
  const { data } = await FunwooAPI.buildingApi.getBuildingShowcases();
  const { data: areaData } = await FunwooAPI.optionApi.getOptionsForBuildingFilter();

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      showcases: data,
      areaData,
    },
  };
};

const Showcase: React.FC<BuildingShowcaseEntity & { className?: string }> = ({
  sid,
  buildingImage,
  adTitle,
  country,
  state,
  buildingState,
  completeYear,
  completeMonth,
  className,
  listing,
}) => {
  const { value: _locationData } = useAsync(async () => {
    return locationData;
  }, []);

  const stateName = useMemo(() => {
    if (state) {
      const stateData = _locationData?.[country]?.find((res) => res.key === state);
      return stateData ? `${stateData.zh_tw} ${stateData.en}` : '';
    } else {
      return state;
    }
  }, [_locationData, state]);

  const _buildingState = useMemo(() => {
    const today = new Date();
    const completeDate = new Date(completeYear, completeMonth, 0);

    if (isBefore(today, completeDate)) {
      return buildingState?.concat({
        state: BuildingStateTag.PRESALE,
        id: 'PRESALE',
        expiredAt: new Date(completeYear, completeMonth, 0).toISOString(),
        buildingId: 'PRESALE',
        createdAt: new Date().toISOString(),
      });
    } else {
      return buildingState;
    }
  }, [buildingState, completeYear, completeMonth]);

  const displayImage = useMemo(
    () => buildingImage?.filter((image) => isNotSet(image.tag))?.[0]?.watermark ?? '',
    [buildingImage]
  );

  const priceByFloorPlan = usePriceByFloorPlan(listing);

  const minimumPrice = useMemo(() => {
    const minPrice = Math.min(...priceByFloorPlan.map(({ min }) => min));
    if (isNaN(minPrice) || !Number.isFinite(minPrice)) return 0;
    return minPrice;
  }, [priceByFloorPlan]);
  const onSaleListingsFloorPlan = useMemo(
    () => priceByFloorPlan.map(({ planType }) => planType).join('、'),
    [priceByFloorPlan]
  );
  const allFloorPlanTypes = useMemo(
    () =>
      uniq(listing.map(({ floor_plan_type }) => floor_plan_type ?? ''))
        .filter(isNotEmptyString)
        .map(capitalize)
        .sort((a, b) => sortListingPlanType(a, b))
        .join('、'),
    [listing]
  );

  return (
    <Link href={`/building/${sid}`} passHref>
      <a
        target={'_blank'}
        rel={'noreferrer noopener'}
        className={classNames(
          className,
          'group',
          'block',
          'rounded',
          'shadow-itemCard hoverable:hover:shadow-itemCardFocus transition-[shadow,transform] duration-300',
          'overflow-hidden'
        )}
      >
        <FunwooImage
          useDefaultPlaceholder
          src={displayImage}
          height={2}
          width={3}
          className={'w-full'}
          imageClassName={'group-hover:group-hover:scale-105 transition-transform duration-300'}
          layout={'fill'}
          alt={adTitle}
        >
          <StateLabels state={_buildingState} />
        </FunwooImage>
        <div className={'px-3 pt-2 pb-4'}>
          <Flexbox as={'p'} align={'center'} className={'text-sm text-gold'}>
            <span>{CountryEnum[country]}</span>
            <span className={'mx-1'}>｜</span>
            <span>{stateName}</span>
          </Flexbox>
          <p className={'mb-2 pb-1 text-lg font-medium border-b border-gray300'}>{adTitle}</p>
          <p className={'mb-4 text-base text-gray700'}>
            {minimumPrice === 0
              ? '請來電洽詢'
              : `$${chineseNumeralFormatter(minimumPrice, country)}${dollarLabelFormatter(country, '台幣')}起`}
          </p>
          <p className={'mb-4 font-noto text-base text-gray700'}>
            {isNotEmptyString(onSaleListingsFloorPlan) ? onSaleListingsFloorPlan : allFloorPlanTypes}
          </p>
          <Button w={'full'}>瞭解更多</Button>
        </div>
      </a>
    </Link>
  );
};

const StateWording: Record<BuildingStateTag, string> = {
  [BuildingStateTag.PRESALE]: '預售屋',
  [BuildingStateTag.NEW]: '新建案',
  [BuildingStateTag.EXCLUSIVE]: '獨家',
  [BuildingStateTag.OPEN_HOUSE]: 'Open House',
  [BuildingStateTag.SOLD_OUT]: '完銷',
};

const StateLabels: React.FC<{ state: BuildingShowcaseEntity['buildingState'] }> = ({ state }) => {
  // show only the sold-out badge if the building is sold out, same as sold listings
  if (isBuildingSoldOut(state)) return <BuildingSoldOutBadge />;

  return (
    <div className={'absolute top-2 left-2'}>
      {state?.map((state) => (
        <span
          key={state.id}
          className={classNames('mr-1 last:mr-0', 'py-1 px-2', 'text-xs font-bold', {
            'bg-white text-gold': state.state !== BuildingStateTag.PRESALE,
            'bg-brand text-white': state.state === BuildingStateTag.PRESALE,
          })}
        >
          {StateWording[state.state]}
        </span>
      ))}
    </div>
  );
};

export const BuildingShowcaseCard = Showcase;
